// Phone photos (12+ megapixels, 3-5 MB) are scaled down before upload: far less mobile data,
// and Strapi processes them in well under a second instead of ~3 s. Photos uploaded at this
// size can't be enlarged later, so raise the limit here if QA needs to zoom in further.
export const MAX_IMAGE_DIMENSION = 2560;
const JPEG_QUALITY = 0.85;

const SHRINKABLE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

/**
 * Returns a JPEG no larger than maxDimension on its longest side, or the original file when
 * it's already small enough, isn't a photo (e.g. a PDF) or the browser can't process it.
 * Applies the photo's EXIF rotation, so it isn't uploaded sideways.
 */
export async function shrinkImage(file, maxDimension = MAX_IMAGE_DIMENSION) {
  if (!SHRINKABLE_TYPES.includes(file.type) || typeof createImageBitmap !== "function") return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  try {
    const scale = maxDimension / Math.max(bitmap.width, bitmap.height);
    if (scale >= 1) return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext("2d");
    if (!context) return file;
    // JPEG has no transparency; fill so transparent PNG areas don't turn black
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap.close?.();
  }
}
